import { ArrowNarrowRightIcon } from '@heroicons/react/solid';
import { QueryDatabaseResponse } from '@notionhq/client/build/src/api-endpoints';
import { m } from 'framer-motion';
import { Link } from 'remix';
import invariant from 'tiny-invariant';

type CardProps = QueryDatabaseResponse['results'][number] & {
  isExternal?: boolean;
};

const LinkWrapper = ({
  children,
  link,
  slug,
  isExternal,
  ...props
}: {
  children: React.ReactNode;
  className: string;
  isExternal: boolean;
  link: QueryDatabaseResponse['results'][number]['properties']['link'];
  slug: QueryDatabaseResponse['results'][number]['properties']['slug'];
}) => {
  const animate = {
    whileHover: 'hover',
    variants: {
      hover: { transform: 'translateY(-2%)' },
    },
  };

  if (isExternal) {
    invariant(
      link?.type === 'url' && link?.url,
      'external links should have an url',
    );

    return (
      <m.a
        href={link?.url}
        target="_blank"
        rel="noreferrer"
        {...animate}
        {...props}
      >
        {children}
      </m.a>
    );
  }

  invariant(
    slug?.type === 'rich_text' && slug?.rich_text?.[0]?.plain_text,
    'internal links should have a slug',
  );

  return (
    <MotionLink to={slug.rich_text?.[0]?.plain_text} {...animate} {...props}>
      {children}
    </MotionLink>
  );
};

const MotionLink = m(Link);

export const Card = ({
  id,
  cover,
  properties,
  isExternal = false,
}: CardProps) => {
  invariant(properties?.title?.type === 'title');
  invariant(properties?.slug?.type === 'rich_text');
  invariant(
    cover?.type === 'external' || cover?.type === 'file',
    'cover source is not external or file',
  );

  let imgUrl;

  if (cover?.type === 'external') {
    imgUrl = cover?.external?.url;
  } else if (cover?.type === 'file') {
    imgUrl = cover?.file?.url;
  }

  const title = properties?.title.title?.[0]?.plain_text;

  invariant(title && imgUrl, 'whoops title, slug or cover are undefined');

  return (
    <LinkWrapper
      key={id}
      link={properties?.link}
      slug={properties?.slug}
      isExternal={isExternal}
      className="relative flex flex-col justify-between bg-secondary shadow-xl rounded-lg overflow-hidden py-4 px-6 max-w-sm w-full h-48 cursor-pointer group transition-opacity hover:opacity-75"
    >
      <div className="absolute w-full h-full gradient filter blur-xl" />
      <m.div
        className="absolute top-0 left-0 h-full w-full max-h-full bg-transparent"
        whileHover={{ scale: 1.2, transform: 'translateY(-2%)' }}
      >
        <img
          src={imgUrl}
          alt="card-cover"
          style={{
            minWidth: '100%',
            minHeight: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: 2,
          }}
        />
      </m.div>
      <div className="self-end group-hover:animate-link z-10">
        <ArrowNarrowRightIcon className="h-6 w-6" />
      </div>
      <div className="z-10 bg-primary bg-opacity-80 -ml-6 py-1 px-6 rounded-tr-lg rounded-br-lg max-w-max group-hover:text-light">
        <h4 className="tracking-wider line-clamp-3"> {title} </h4>
      </div>
    </LinkWrapper>
  );
};
